<template>
    <div
        v-click-outside="{
            callback: closeStats,
            when: expandStats,
        }"
        :class="[
            { 'cursor-pointer': !expandStats },
            'flashcards-stats z-[9] mb-3 rounded-lg bg-white shadow md:absolute',
        ]"
    >
        <div
            v-if="userHasVisited"
            class="flex items-center justify-start p-3"
            @click="toggleExpandedStats"
        >
            <div class="mr-4 flex">
                <i :class="[{ open: expandStats }, 'open-indicator icon icon-chevron-down bg-slate-400']"></i>
            </div>
            <div class="flex pr-3">
                <UserAvatar
                    :user-id="user.userid"
                    :animated-avatar="user.gamify_avatar_url"
                    :picture="user.picture"
                    :show-admin-badge="user.is_admin"
                    size="xs"
                >
                </UserAvatar>
            </div>
            <div class="flex-none pr-3 text-sm text-slate-400">
                <span class="text-green-500">{{ totalOfCorrectCards }}</span
                >/{{ totalOfNotHiddenCards }}
            </div>
            <div class="stat-bar-outer w-80 flex-grow">
                <div
                    v-for="(statValue, statName) in notHiddenStats"
                    :key="statName"
                    :class="[`stat-bar-${statName}`, 'stat-bar-inner']"
                    :style="{ width: calculateBarWidth(statValue) }"
                ></div>
            </div>
        </div>

        <div :class="[{ open: expandStats }, 'expanded-stats flex flex-wrap px-3']">
            <div class="flex w-full items-center border-b text-sm text-slate-400">
                <i class="icon icon-checkmark-outlined mr-3 h-5 w-5 bg-green-500"></i>
                <span :class="`text-green-500`"
                    >{{ $t(`flashcards:detailsStats.assessments.correct`) }}: {{ stats.correct }}</span
                >/{{ totalOfNotHiddenCards }}
            </div>
            <div class="flex w-full items-center border-b text-sm text-slate-400">
                <i class="icon icon-approximately mr-3 h-5 w-5 bg-orange-300"></i>
                <span class="text-orange-300"
                    >{{ $t(`flashcards:detailsStats.assessments.unsure`) }}: {{ stats.unsure }}</span
                >/{{ totalOfNotHiddenCards }}
            </div>
            <div class="flex w-full items-center border-b text-sm text-slate-400">
                <i class="icon icon-incorrect mr-3 h-5 w-5 bg-red-600"></i>
                <span class="text-red-600"
                    >{{ $t(`flashcards:detailsStats.assessments.incorrect`) }}: {{ stats.incorrect }}</span
                >/{{ totalOfNotHiddenCards }}
            </div>
            <div class="flex w-full items-center border-b text-sm text-slate-400">
                <i class="icon icon-pause mr-3 h-5 w-5 bg-slate-400"></i>
                <span class="text-slate-400"
                    >{{ $t(`flashcards:detailsStats.assessments.not_assessed`) }}: {{ stats.not_assessed }}</span
                >/{{ totalOfNotHiddenCards }}
            </div>
            <div class="px-4px py-14px flex w-full items-center text-sm">
                <i class="icon icon-visibility-off mr-3 h-5 w-5"></i>
                <span>{{ $t('flashcards:detailsStats.assessments.hidden') }}: {{ totalOfHiddenCards }}</span>
            </div>

            <!-- native modifier has been removed, please confirm whether the function has been affected  -->

            <BtnCta
                variant="secondary"
                class="mb-3 w-full"
                size="xs"
                :label="$t('flashcards:detailsStats.reset')"
                @click="resetProgress"
            />
        </div>
    </div>
</template>

<script>
import { get } from 'lodash-es';

export default {
    props: {
        flashcardSet: {
            type: Object,
            required: true,
        },
        userHasVisited: {
            default: false,
            required: true,
            type: Boolean,
        },
        user: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            expandStats: false,
            stats: get(sdWindow, 'flashcards.stats', {}),
        };
    },

    computed: {
        authUser() {
            return get(sdWindow, 'flashcards.user');
        },

        authUserPicture() {
            if (!this.authUser) {
                return null;
            }

            return this.authUser.picture ? this.authUser.picture : this.authUser.avatar_picture;
        },

        isAnonymousSet() {
            return this.flashcardSet.is_anonymous === 1;
        },

        notHiddenStats() {
            const { hidden, ...notHiddenStats } = this.stats;
            return notHiddenStats;
        },

        totalOfNotHiddenCards() {
            return Object.entries(this.notHiddenStats).reduce((total, stat) => total + stat[1], 0);
        },

        totalOfCorrectCards() {
            const { correct } = this.stats;
            return correct;
        },

        totalOfHiddenCards() {
            const { hidden } = this.stats;

            return hidden;
        },

        studyPageUrl() {
            return get(sdWindow, 'flashcards.set.link_study_page', '#');
        },
        modalTranslations() {
            return {
                title: this.$t('flashcards:resetConfirmModal.title'),
                submitText: this.$t('flashcards:resetConfirmModal.submitText'),
            };
        },
    },

    created() {
        this.setEventListeners();
    },

    methods: {
        getStats() {
            const url = `/flashcards/sets/${this.flashcardSet.id}/stats`;

            this.$http
                .get(url)
                .then((response) => {
                    this.stats = response.data.stats;
                })
                .catch((error) => console.error(error));
        },

        calculateBarWidth(statValue) {
            if (this.totalOfNotHiddenCards > 0) {
                return `${(statValue / this.totalOfNotHiddenCards) * 100}%`;
            }

            return 0;
        },

        toggleExpandedStats() {
            this.expandStats = !this.expandStats;
        },

        async resetProgress() {
            const confirmed = await this.$modal.confirm({
                title: this.modalTranslations.title,
                submitText: this.modalTranslations.submitText,
            });

            if (!confirmed) return;

            const url = `/flashcards/sets/${this.flashcardSet.id}/studies/reset`;

            this.$http
                .post(url)
                .then(() => window.location.reload())
                .catch(() => console.error('Could not reset the stats.'));
        },

        closeStats() {
            if (!this.expandStats) {
                return;
            }

            this.expandStats = false;
        },

        checkUserCanStudy(event) {
            if (!this.authUser) {
                event.preventDefault();
                window.bus.emit('prompt-signup', {});
            }
        },

        setEventListeners() {
            window.bus.on('flashcards-update-stats', this.getStats);
        },
    },
};
</script>
<style lang="scss" scoped>
.flashcards-stats {
    max-width: 100%;
    width: 26.5rem;
}
.stat-bar-outer {
    border-radius: 2px;
    height: 4px;
    overflow: hidden;
}

.stat-bar-inner {
    @apply bg-slate-200;
    border-bottom-right-radius: 2px;
    border-top-right-radius: 2px;
    float: left;
    height: 100%;
    margin-left: -1px;
    position: relative;
    transition: all 1s ease-out;

    // apply a reverse z-index order
    // to have a "stack" effect
    @for $i from 1 through 5 {
        &:nth-child(#{$i}) {
            z-index: #{5 - $i};
        }
    }
}

.stat-bar-correct {
    @apply bg-green-500;
}

.stat-bar-incorrect {
    @apply bg-red-600;
}

.stat-bar-unsure {
    @apply bg-orange-300;
}

.open-indicator {
    transition: transform 0.15s ease-out;
    &.open {
        opacity: 0.75;
        transform: rotate(180deg);
    }
}

.expanded-stats {
    height: 0;
    overflow: hidden;

    &.open {
        animation: slideDown 0.2s ease-out forwards;

        @keyframes slideDown {
            to {
                height: 330px;
            }
        }
    }
}
</style>
